/**
 * A reusable function to filter and sort a master list.
 *
 * @param {Array} masterList - The original, unfiltered, unsorted array
 * @param {String} searchVal - The user's search term
 * @param {Array} searchableKeys - The keys in the masterList to search (e.g., ['displayName'])
 * @param {String} sortKey - The th key to sort by (e.g., 'adult')
 * @param {String} sortOrder - 'asc' or 'desc'
 * @param {Object} keyMap - A map to translate th keys to data keys (e.g., { adult: 'displayName' })
 * @returns {Array} - The new filtered and sorted array
 */
export default function handleSortSearchSimpleTableList(
  masterList,
  searchVal,
  searchableKeys,
  sortKey,
  sortOrder,
  keyMap,
) {
  // 1. Start with the full list
  const list = [...masterList];

  // 2. FILTERING
  let filteredList = list;
  if (searchVal) {
    const searchLower = searchVal.toLowerCase();
    // Use 'searchableKeys' to check against the source data
    filteredList = list.filter((person) => searchableKeys.some((key) => {
      const value = person[key];
      return value && typeof value === 'string' && value.toLowerCase().includes(searchLower);
    }));
  }

  // 3. SORTING
  if (sortKey) {
    const sourceSortKey = keyMap[sortKey];

    if (sourceSortKey) {
      filteredList.sort((a, b) => {
        const valA = a[sourceSortKey];
        const valB = b[sourceSortKey];
        const order = sortOrder === 'asc' ? 1 : -1;

        // 1. Check for "empty-ish" values (null or undefined)
        // We treat empty strings '' as valid, sortable strings
        const aIsNull = valA == null;
        const bIsNull = valB == null;

        if (aIsNull && bIsNull) return 0; // Both are null

        // Null values should respect the sort order
        if (aIsNull) return 1 * order; // 'asc' sends nulls to end, 'desc' to start
        if (bIsNull) return -1 * order; // 'asc' sends nulls to end, 'desc' to start

        // Check for Number sorting
        if (typeof valA === 'number' && typeof valB === 'number') {
          return (valA - valB) * order;
        }

        // Default to String sorting (case-insensitive)
        const stringA = String(valA).toLowerCase();
        const stringB = String(valB).toLowerCase();

        if (stringA < stringB) return -1 * order;
        if (stringA > stringB) return 1 * order;
        return 0;
      });
    }
  }

  // 4. Return the final processed list
  return filteredList;
}
